import { useState, useRef, useEffect } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Browse Categories');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleCategorySelect = (cat: typeof categories[0]) => {
    setSelectedCategory(cat.name);
    setDropdownOpen(false);
    window.open(cat.url, '_blank', 'noopener,nofollow');
  };

  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Fashion%20streetwear%20flatlay%20composition%20with%20sneakers%20hoodies%20caps%20and%20accessories%20arranged%20on%20clean%20cream%20textured%20background%2C%20editorial%20product%20photography%2C%20soft%20natural%20window%20lighting%20from%20left%2C%20warm%20neutral%20color%20palette%20with%20earth%20tones%2C%20minimal%20aesthetic%20composition%2C%20high%20detail%20textile%20textures%20visible%2C%20professional%20catalog%20styling%2C%20harmonious%20arrangement&width=1600&height=900&seq=hero-bg-2026&orientation=landscape"
          alt="CSSBuy Spreadsheet curated fashion finds flatlay"
          title="CSSBuy Spreadsheet — Verified Product Database"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>
      </div>

      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 pt-24 pb-16">
        <div className="max-w-2xl">
          <span className="inline-block px-4 py-1.5 border border-white/30 rounded-full text-white/90 text-xs md:text-sm font-medium tracking-wide mb-6 whitespace-nowrap">
            Verified CSSBuy Spreadsheet — Updated Daily
          </span>

          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white font-semibold leading-tight md:leading-tight mb-4">
            The Ultimate<br />
            <span className="italic font-normal">CSSBuy Spreadsheet</span><br />
            Database
          </h1>

          <p className="text-white/75 text-sm md:text-base max-w-lg mb-8 leading-relaxed">
            Browse thousands of verified product listings from Taobao, Weidian, and 1688 — organized by category, brand, and price. Your trusted CSSBuy shopping companion for finding the best reps and original pieces.
          </p>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-8 flex-wrap">
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="inline-flex items-center justify-between gap-3 px-5 py-3 bg-white/15 backdrop-blur-md border border-white/25 rounded-xl text-white text-sm font-medium min-w-[240px] hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span>{selectedCategory}</span>
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line"></i>
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-[280px] bg-white rounded-xl border border-background-200 overflow-hidden z-20 max-h-[420px] overflow-y-auto">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategorySelect(cat)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap border-b border-background-100 last:border-b-0"
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                        <i className={cat.icon}></i>
                      </span>
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <span className="text-white/60 text-sm hidden sm:inline whitespace-nowrap">or jump to</span>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              cssbuy sheet
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line"></i></span>
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-white text-sm font-medium rounded-full hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              buy it now
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line"></i></span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}